// t() reads the page language, which the compiler cannot see, and this stays
// on screen while the language changes.
"use no memo";
import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { useEffect, useRef, useState, type ReactNode } from "react";
import Download from "~icons/ph/download-simple";
import Share2 from "~icons/ph/share-network";
import ShieldCheck from "~icons/ph/shield-check";
import X from "~icons/ph/x";
import { CopyButton } from "../ui/components";
import { notify } from "../ui/toast";
import { t } from "../i18n";

// Only when the reminder was closed is saved here. Recovery codes stay out of
// browser storage.
const reminderKey = (id: string) => `ranna:recovery-reminder:v2:${id}`;
const reminderReturnsAfterMs = 14 * 86400000;

export function useRecoveryReminder(
  id: string | undefined,
  canReturn: boolean,
  hasFriends: boolean,
) {
  const [dismissed, setDismissed] = useState<{ id?: string; value: boolean }>({
    value: false,
  });
  useEffect(() => {
    if (!id) return;
    const read = () => {
      let value = false;
      try {
        const closedAt = Number(localStorage.getItem(reminderKey(id)));
        value = closedAt + reminderReturnsAfterMs > Date.now();
      } catch {}
      setDismissed({ id, value });
    };
    read();
    const sync = (event: StorageEvent) => {
      if (event.key === reminderKey(id) || event.key === null) read();
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [id]);

  return {
    visible:
      !!id &&
      dismissed.id === id &&
      !dismissed.value &&
      !canReturn &&
      hasFriends,
    dismiss: () => {
      if (!id) return;
      setDismissed({ id, value: true });
      try {
        localStorage.setItem(reminderKey(id), String(Date.now()));
      } catch {}
    },
  };
}

export function RecoveryReminder({
  onSave,
  onDismiss,
  disabled,
}: {
  onSave: () => void;
  onDismiss: () => void;
  disabled: boolean;
}) {
  const passkeys = browserSupportsWebAuthn();
  const title = t("لا توجد طريقة للعودة إلى حسابك بعد");
  return (
    <aside className="recovery-reminder" aria-label={title}>
      <ShieldCheck width={20} height={20} aria-hidden="true" />
      <div>
        <h3>{title}</h3>
        <p>
          {t(
            passkeys
              ? "إذا مسحت بيانات المتصفح أو فقدت جهازك أو غبت عنه سنة، ستفقد حسابك وأصدقاءك ومحادثاتك. مفتاح المرور يعيدك إليه ببصمتك أو وجهك أو قفل الشاشة."
              : "إذا مسحت بيانات المتصفح أو فقدت جهازك أو غبت عنه سنة، ستفقد حسابك وأصدقاءك ومحادثاتك. احفظ رمز الاستعادة في مكان خاص لتعود به من أي جهاز.",
          )}
        </p>
        <button className="quiet-button" disabled={disabled} onClick={onSave}>
          {t(passkeys ? "إضافة مفتاح مرور" : "حفظ رمز الاستعادة")}
        </button>
      </div>
      <button
        className="icon-button"
        aria-label={t("ذكّرني لاحقًا")}
        title={t("ذكّرني لاحقًا")}
        onClick={onDismiss}
      >
        <X width={16} height={16} />
      </button>
    </aside>
  );
}

export function RecoveryCode({
  code,
  username,
}: {
  code: string;
  username: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  function download() {
    const file = new Blob(
      [
        `Ranna\n@${username}\n\n${t("رمز الاستعادة")}: ${code}\n\n${t("احتفظ بهذا الرمز في مكان آمن. يتيح لك الدخول من أي جهاز، وإنشاء رمز جديد يلغي السابق.")}\n`,
      ],
      { type: "text/plain;charset=utf-8" },
    );
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ranna-recovery-${username}.txt`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
  return (
    <>
      <input
        ref={input}
        readOnly
        dir="ltr"
        aria-label={t("رمز الاستعادة الجديد")}
        value={code}
        onFocus={(e) => e.target.select()}
        autoComplete="off"
        spellCheck={false}
      />
      <div className="account-code-actions">
        <CopyButton
          className="quiet-button"
          text={code}
          label={t("نسخ الرمز")}
          iconSize={14}
          onResult={(copied) => {
            if (copied) return;
            input.current?.focus();
            input.current?.select();
            notify(t("حدد الرمز وانسخه يدويًا."));
          }}
        />
        <button className="quiet-button" onClick={download}>
          <Download width={14} height={14} />
          {t("تنزيل الرمز")}
        </button>
      </div>
    </>
  );
}

export function ShareLink({
  label,
  link,
  text,
  inputId,
  children,
}: {
  label: string;
  link: string;
  text?: string;
  inputId?: string;
  children?: ReactNode;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [feedback, setFeedback] = useState("");
  const [sharing, setSharing] = useState(false);
  async function share() {
    if (sharing) return;
    setSharing(true);
    setFeedback("");
    try {
      await navigator.share({ title: t("رنّة"), text, url: link });
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError"))
        setFeedback(t("تعذرت المشاركة. انسخ الرابط وأرسله إلى صديقك."));
    } finally {
      setSharing(false);
    }
  }
  return (
    <div className="share-link">
      <label>
        {label}
        <input
          ref={input}
          id={inputId}
          className="copy-link"
          readOnly
          dir="ltr"
          value={link}
          onFocus={(e) => e.target.select()}
          autoComplete="off"
          spellCheck={false}
        />
      </label>
      <div className="share-link-actions">
        <CopyButton
          className="button"
          text={link}
          label={t("نسخ الرابط")}
          onResult={(copied) => {
            setFeedback(copied ? "" : t("حدد الرابط وانسخه يدويًا."));
            if (copied) return;
            input.current?.focus();
            input.current?.select();
          }}
        />
        {typeof navigator.share === "function" && (
          <button
            className="button secondary"
            disabled={sharing}
            onClick={() => void share()}
          >
            <Share2 width={16} height={16} />
            {t("مشاركة…")}
          </button>
        )}
        {children}
      </div>
      <p className="copy-feedback" role="status">
        {feedback}
      </p>
    </div>
  );
}
